import styled from "styled-components";

import {
    distances,
    globalMobileBreakpoint,
    globalSmallMobileBreakpoint,
    palette,
    shadows,
} from "../../styles/constants";
import View, { ViewProps } from "../View";

type ButtonGroupProps = {
    $stickyActionsMargin?: string;
    $groupValue?: string;
    $color?: keyof typeof palette;
};

const ButtonGroup = styled(View).attrs<ViewProps>((props) => ({
    ...props,
    justify: props.justify || "flex-end",
    gap: distances.small,
    className: "button-group",
}))<ButtonGroupProps>`
    flex-wrap: wrap;

    &.sticky {
        position: sticky;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(1px);
        white-space: nowrap;
        flex-wrap: nowrap;
        padding-block: ${distances.normal24};
        border-top: solid 1px rgba(0, 0, 0, 0.1);
        z-index: 10;
    }

    @media (max-width: ${globalMobileBreakpoint}px) {
        justify-content: center;
        flex-direction: column;
        & > button, & > a {
            width: 100%;
            flex: 1;
        }
        & > div {
            width: 100%;
            flex: 1;
            button, a {
                width: 100%;
            }
        }
    }
    &[data-sticky-actions="true"] {
        position: sticky;
        bottom: -${distances.normal24};
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(1px);
        margin-inline: -${distances.normal24};
        margin-bottom: -${distances.normal24};
        padding-inline: ${distances.normal24};
        padding-block: ${distances.normal24};
        border-top: solid 1px rgba(0, 0, 0, 0.1);
        @media (max-width: ${globalMobileBreakpoint}px) {
            bottom: -${distances.small};
            margin-inline: -${distances.small};
            margin-bottom: -${distances.small};
            padding-inline: ${distances.small};
            padding-block: ${distances.small};
        }
    }

    &[data-sticky-toolbar-actions="true"] {
        position: sticky;
        top: -${distances.normal};
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(1px);
        margin-inline: -${distances.normal24};
        margin-top: -${distances.normal24};
        padding-inline: ${distances.normal24};
        padding-block: ${distances.tiny};
        border-bottom: solid 1px rgba(0, 0, 0, 0.1);
        white-space: nowrap;
        flex-wrap: nowrap;
        @media (max-width: ${globalMobileBreakpoint}px) {
            top: -${distances.small};
            margin-inline: -${distances.small};
            margin-top: -${distances.small};
            padding-inline: ${distances.small};
            padding-block: ${distances.small};
        }
    }

    &[data-grouped-actions="true"] {
        flex-wrap: nowrap;
        gap: 0;
        box-shadow: ${shadows.xsmall};
        border-radius: 8px;
        flex-direction: row;
        max-width: max-content;
        button {
            border-radius: 0;
            background: ${palette.white};
            border-color: ${palette.neutral[200]};
            margin-inline: -0.5px;
            color: ${palette.neutral[900]};
            outline: none;
            white-space: nowrap;
            &:first-child {
                border-top-left-radius: 8px;
                border-bottom-left-radius: 8px;
            }
            &:last-child {
                border-top-right-radius: 8px;
                border-bottom-right-radius: 8px;
            }
            &:hover, &:focus-visible {
                background: ${palette.neutral[50]};
                border-color: ${palette.neutral[300]};
                color: ${palette.neutral[900]};
                z-index: 2;
            }
            &.disabled, &:disabled {
                opacity: 0.6;
                pointer-events: none;
            }
        }
        button[value="${(props) => props?.$groupValue}"] {
            background: ${(props) => palette[props.$color || "primary"][700]};
            border-color: ${(props) => palette[props.$color || "primary"][700]};
            color: ${palette.white};
            z-index: 1;
            &:hover {
                background: ${(props) => palette[props.$color || "primary"][500]};
                border-color: ${(props) => palette[props.$color || "primary"][500]};
                color: ${palette.white};
            }
        }
        @media (max-width: ${globalSmallMobileBreakpoint}px) {
            justify-content: center;
            max-width: unset;
            & > button {
                width: 100%;
                flex: 1;
            }
            & > div {
                width: 100%;
                flex: 1;
                button {
                    width: 100%;
                }
            }
        }
    }
`;

export default ButtonGroup;
